import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, List, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@vaiu/ui/collapsible';
import TransactionHistoryPage from '@/pages/TransactionHistoryPage';

class RecentActivityErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('❌ RecentActivity Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-center">
          <p className="text-red-600 mb-2">Error loading recent activity</p>
          <button 
            onClick={() => {
              this.setState({ hasError: false });
              window.location.reload();
            }} 
            className="text-blue-600 underline text-sm"
          >
            Refresh page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const RecentActivity: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'chronological' | 'grouped'>('chronological');
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <RecentActivityErrorBoundary>
      <Card>
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <div className="flex items-center space-x-2">
                  {isExpanded && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewMode(viewMode === 'chronological' ? 'grouped' : 'chronological');
                      }}
                      className="h-8 px-2"
                    >
                      {viewMode === 'chronological' ? (
                        <List className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>
              </div>
              {!isExpanded && (
                <p className="text-sm text-muted-foreground text-left">
                  Click to view your recent transactions
                </p>
              )}
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="p-0">
              {/* Only render TransactionHistoryPage when expanded */}
              {isExpanded && (
                <TransactionHistoryPage
                  embedded={true}
                  hideFilters={true}
                  limit={20}
                  showPagination={false}
                  sortMode={viewMode === 'chronological' ? 'chronological' : 'grouped'}
                  onSeeAll={() => navigate('/transactions')}
                  shouldFetch={isExpanded}
                />
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </RecentActivityErrorBoundary>
  );
};

export default RecentActivity;
